import type { CatalogPendingIngredient, CatalogPendingItem, CatalogPendingSupplier, IngredientInput, SupplierInput } from '../types/inventory';

// 26A-3: lo que el empleado definió en la propuesta, listo para mostrarse junto al nombre en la bandeja.
// Puro (sin React) para poder probarlo. Los campos vacíos se omiten; la unidad base siempre se muestra
// porque siempre existe y es lo que más cuesta corregir después (queda bloqueada al primer movimiento).
export interface ProposalDetail { label: string; value: string }

// Tolerante a un backend anterior a 26A-3 (la web estática se publica antes que el backend en Render):
// si la propuesta llega sin estos campos, simplemente no se muestran; la página no se cae.
export function describeProposal(item: CatalogPendingItem): ProposalDetail[] {
  const out: ProposalDetail[] = [];
  if (item.type === 'INGREDIENT') {
    if (item.baseUnit) out.push({ label: 'Unidad base', value: `${item.baseUnit.name} (${item.baseUnit.symbol})` });
    if (item.baseUnit) out.push({ label: 'Categoría', value: item.category?.trim() || 'Sin categoría' });
    if (item.sku?.trim()) out.push({ label: 'SKU', value: item.sku.trim() });
    if (item.description?.trim()) out.push({ label: 'Descripción', value: item.description.trim() });
    return out;
  }
  if (item.contactName?.trim()) out.push({ label: 'Contacto', value: item.contactName.trim() });
  if (item.phone?.trim()) out.push({ label: 'Teléfono', value: item.phone.trim() });
  if (item.email?.trim()) out.push({ label: 'Email', value: item.email.trim() });
  if (item.notes?.trim()) out.push({ label: 'Notas', value: item.notes.trim() });
  return out;
}

// Formulario único de "Editar y aprobar": cada tipo usa solo sus campos.
export interface ApprovalEditForm { name: string; baseUnitId: string; category: string; sku: string; contactName: string; phone: string; email: string }

export function editFormFor(item: CatalogPendingItem): ApprovalEditForm {
  if (item.type === 'INGREDIENT') {
    return { name: item.name, baseUnitId: item.baseUnit?.id ?? '', category: item.category ?? '', sku: item.sku ?? '', contactName: '', phone: '', email: '' };
  }
  return { name: item.name, baseUnitId: '', category: '', sku: '', contactName: item.contactName ?? '', phone: item.phone ?? '', email: item.email ?? '' };
}

const norm = (value: string | null | undefined) => value?.trim() || null;

// Solo viaja lo que cambió: el backend conserva la propuesta original en proposedPayloadJson y un diff
// mantiene legible la auditoría. Devuelve undefined si no hay cambios (aprobación simple).
export function ingredientApprovalEdits(item: CatalogPendingIngredient, form: ApprovalEditForm): Partial<IngredientInput> | undefined {
  const edits: Partial<IngredientInput> = {};
  const name = form.name.trim();
  if (name && name !== item.name) edits.name = name;
  if (form.baseUnitId && form.baseUnitId !== item.baseUnit?.id) edits.baseUnitId = form.baseUnitId;
  if (norm(form.category) !== norm(item.category)) edits.category = norm(form.category);
  if (norm(form.sku) !== norm(item.sku)) edits.sku = norm(form.sku);
  return Object.keys(edits).length ? edits : undefined;
}

export function supplierApprovalEdits(item: CatalogPendingSupplier, form: ApprovalEditForm): Partial<SupplierInput> | undefined {
  const edits: Partial<SupplierInput> = {};
  const name = form.name.trim();
  if (name && name !== item.name) edits.name = name;
  if (norm(form.contactName) !== norm(item.contactName)) edits.contactName = norm(form.contactName);
  if (norm(form.phone) !== norm(item.phone)) edits.phone = norm(form.phone);
  if (norm(form.email) !== norm(item.email)) edits.email = norm(form.email);
  return Object.keys(edits).length ? edits : undefined;
}
